
import { supabase } from "@/integrations/supabase/client";

export const VALID_CONTENT_TYPES = [
  "webpage",
  "article", 
  "video",
  "image",
  "pdf",
  "document",
  "text-highlight"
] as const;

export type ContentType = typeof VALID_CONTENT_TYPES[number];

export const CONTENT_TYPE_OPTIONS = [
  { value: "webpage", label: "Webpage" },
  { value: "article", label: "Article" },
  { value: "video", label: "Video" },
  { value: "image", label: "Image" },
  { value: "pdf", label: "PDF" },
  { value: "document", label: "Document" },
  { value: "text-highlight", label: "Highlighted Text" }
];

// Collection ID constants
export const INBOX_ID = "inbox";
export const ARCHIVE_ID = "archive";

export const getDefaultContentType = (): ContentType => "webpage";

export const validateContentType = (type: string): type is ContentType => {
  return VALID_CONTENT_TYPES.includes(type as ContentType);
};

// Helper to detect content type from URL
export const detectContentTypeFromUrl = (url: string): ContentType => {
  if (!url) return "webpage";
  
  const lowercaseUrl = url.toLowerCase();
  
  // Check for common file extensions
  if (lowercaseUrl.includes('.pdf')) return "pdf";
  if (lowercaseUrl.match(/\.(jpg|jpeg|png|gif|webp|svg)$/)) return "image";
  if (lowercaseUrl.match(/\.(mp4|webm|avi|mov|wmv|flv)$/)) return "video";
  if (lowercaseUrl.match(/\.(doc|docx|txt|rtf)$/)) return "document";
  
  // Check for video platforms
  if (lowercaseUrl.includes('youtube.') || lowercaseUrl.includes('youtu.be/')) return "video";
  if (lowercaseUrl.includes('vimeo.com')) return "video";
  if (lowercaseUrl.includes('twitch.tv')) return "video";
  
  // Check for article/blog indicators
  if (lowercaseUrl.includes('/blog/') || lowercaseUrl.includes('/article/') || lowercaseUrl.includes('/post/')) return "article";
  
  // Default to webpage
  return "webpage";
};

// Helper to get display name for content type
export const getContentTypeDisplayName = (type: ContentType): string => {
  const option = CONTENT_TYPE_OPTIONS.find(opt => opt.value === type);
  return option?.label || type;
};

// Form validation function
export const validateContentForm = (url: string, contentType: string, user: any): boolean => {
  if (!url.trim()) {
    console.error("URL is required");
    return false;
  }
  
  if (!validateContentType(contentType)) {
    console.error("Invalid content type");
    return false;
  }
  
  if (!user) {
    console.error("User authentication required");
    return false;
  }
  
  return true;
};

// Content submission function - NOW WITH REAL DATABASE OPERATIONS
export const submitContentToSupabase = async ({
  user,
  url,
  title,
  description,
  contentType,
  selectedCollection,
  isUpdate = false,
  contentId
}: {
  user: any;
  url: string;
  title: string;
  description: string;
  contentType: string;
  selectedCollection: string;
  isUpdate?: boolean;
  contentId?: string;
}): Promise<{ success: boolean; contentId?: string }> => {
  try {
    console.log("Starting content submission to Supabase:", {
      user: user?.id,
      url,
      title,
      description,
      contentType,
      selectedCollection,
      isUpdate,
      contentId
    });

    if (isUpdate && contentId) {
      // Update existing content
      const { data: updatedContent, error: updateError } = await supabase
        .from('content_items')
        .update({
          title: title || 'Untitled',
          url,
          description: description || null,
          content_type: contentType,
        })
        .eq('id', contentId)
        .eq('user_id', user.id)
        .select('id')
        .single();

      if (updateError) {
        console.error('Error updating content:', updateError);
        return { success: false };
      }

      // Update collection association if needed and not inbox
      if (selectedCollection !== INBOX_ID) {
        // Delete existing collection associations
        await supabase
          .from('content_collections')
          .delete()
          .eq('content_id', contentId);

        // Add new collection association
        const { error: collectionError } = await supabase
          .from('content_collections')
          .insert({
            content_id: contentId,
            collection_id: selectedCollection
          });

        if (collectionError) {
          console.error('Error updating collection association:', collectionError);
        }
      }

      console.log('Content updated successfully:', updatedContent.id);
      return { success: true, contentId: updatedContent.id };
    } else {
      // Create new content
      const { data: newContent, error: insertError } = await supabase
        .from('content_items')
        .insert({
          title: title || 'Untitled',
          url,
          description: description || null,
          content_type: contentType,
          user_id: user.id,
          metadata: {
            thumbnail_extraction_status: 'pending'
          }
        })
        .select('id')
        .single();

      if (insertError) {
        console.error('Error creating content:', insertError);
        return { success: false };
      }

      const newContentId = newContent.id;
      console.log('Content created successfully:', newContentId);

      // Associate with collection if not inbox
      if (selectedCollection !== INBOX_ID) {
        const { error: collectionError } = await supabase
          .from('content_collections')
          .insert({
            content_id: newContentId,
            collection_id: selectedCollection
          });

        if (collectionError) {
          console.error('Error associating content with collection:', collectionError);
          // Don't fail the whole operation for collection association errors
        } else {
          console.log('Content associated with collection successfully');
        }
      }

      return { success: true, contentId: newContentId };
    }
  } catch (error) {
    console.error('Unexpected error in submitContentToSupabase:', error);
    return { success: false };
  }
};
